/**
 * ClinicalDocDashboard — outstanding-note report.
 *
 * Shows draft assessments, in-progress / errored SOAP sessions, and a
 * per-practitioner rollup so owners can chase outstanding
 * documentation before the end of the week.
 */
import { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { RefreshCw, FileText, Loader2, Stethoscope, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { BackLink } from "@/components/BackLink";

export default function ClinicalDocDashboard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/health-record/dashboard", {
        params: { from: from || undefined, to: to || undefined },
      });
      setData(r.data);
    } catch (e) {
      toast.error("Load failed", { description: e?.response?.data?.detail || e.message });
    } finally {
      setLoading(false);
    }
  }, [from, to]);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="mx-auto max-w-[1100px] px-6 py-8">
      <BackLink to="/assessments" label="Back to Assessments" testId="docs-dashboard-back" />
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="text-xs font-semibold uppercase tracking-[0.2em] text-zinc-500 flex items-center gap-2">
            <FileText className="h-3 w-3" /> Clinical documentation
          </div>
          <h1 className="mt-1 font-display text-3xl sm:text-4xl font-bold tracking-tight text-zinc-950">
            Outstanding notes
          </h1>
          <p className="mt-2 text-sm text-zinc-600 max-w-2xl">
            Draft assessments, in-progress SOAP sessions and any documentation currently sitting in
            error state. Chase these before end-of-week to keep the record complete.
          </p>
        </div>
        <div className="flex items-end gap-2">
          <div>
            <label className="text-2xs uppercase tracking-wider text-zinc-500">From</label>
            <Input type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="h-9 w-[145px]" />
          </div>
          <div>
            <label className="text-2xs uppercase tracking-wider text-zinc-500">To</label>
            <Input type="date" value={to} onChange={(e) => setTo(e.target.value)} className="h-9 w-[145px]" />
          </div>
          <Button variant="outline" onClick={load} disabled={loading} className="h-9">
            <RefreshCw className={"h-4 w-4 " + (loading ? "animate-spin" : "")} />
          </Button>
        </div>
      </div>

      {loading && !data ? (
        <div className="mt-8 flex items-center gap-2 text-sm text-zinc-500">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading…
        </div>
      ) : !data ? (
        <div className="mt-8 text-sm text-zinc-500">No data.</div>
      ) : (
        <>
          <div className="mt-6 grid grid-cols-1 sm:grid-cols-3 gap-3">
            <Metric icon={Stethoscope} label="Draft assessments" value={data.totals.draft_assessments} tone={data.totals.draft_assessments > 0 ? "warn" : "ok"} testid="metric-draft-assessments" />
            <Metric icon={FileText} label="Draft / stuck SOAP sessions" value={data.totals.draft_soap_sessions} tone={data.totals.draft_soap_sessions > 0 ? "warn" : "ok"} testid="metric-draft-soap" />
            <Metric icon={AlertTriangle} label="Finalised in window" value={data.totals.finalised_assessments_in_window} tone="info" testid="metric-finalised" />
          </div>

          <Card className="mt-6 rounded-lg border border-zinc-200 shadow-none overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-zinc-50">
                <tr className="text-left text-2xs uppercase tracking-wider text-zinc-600">
                  <th className="px-4 py-2">Practitioner</th>
                  <th className="px-4 py-2 text-right">Draft</th>
                  <th className="px-4 py-2 text-right">Finalised</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-zinc-100" data-testid="dashboard-by-practitioner">
                {(data.by_practitioner || []).length === 0 ? (
                  <tr><td colSpan={3} className="px-4 py-6 text-center text-zinc-500">No practitioner activity yet.</td></tr>
                ) : data.by_practitioner.map((row) => (
                  <tr key={row.practitioner_id}>
                    <td className="px-4 py-2 font-medium text-zinc-800">{row.practitioner_name}</td>
                    <td className="px-4 py-2 text-right font-mono-num tabular-nums">
                      <span className={"px-1.5 py-0.5 rounded " + (row.draft > 0 ? "bg-amber-100 text-amber-900" : "text-zinc-500")}>
                        {row.draft}
                      </span>
                    </td>
                    <td className="px-4 py-2 text-right font-mono-num tabular-nums text-zinc-700">{row.finalised}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Card>
        </>
      )}
    </div>
  );
}

function Metric({ icon: Icon, label, value, tone, testid }) {
  const toneCls =
    tone === "warn" ? "text-amber-700 bg-amber-50 border-amber-200"
    : tone === "ok" ? "text-emerald-700 bg-emerald-50 border-emerald-200"
    : "text-sky-700 bg-sky-50 border-sky-200";
  return (
    <Card className="p-4 rounded-lg border border-zinc-200 shadow-none flex items-center gap-3" data-testid={testid}>
      <div className={"h-10 w-10 rounded-md border grid place-items-center " + toneCls}>
        <Icon className="h-5 w-5" />
      </div>
      <div>
        <div className="text-2xs uppercase tracking-wider text-zinc-500 font-semibold">{label}</div>
        <div className="text-2xl font-semibold text-zinc-950 font-mono-num tabular-nums">{value}</div>
      </div>
    </Card>
  );
}
